import type {
  CogoUniverFormulaOracleCase,
  CogoUniverFormulaOracleModel,
} from "./univer-formula-oracle";
import {
  getReportWorkspaceModelCopy,
  type ReportWorkspaceModelCopy,
} from "./report-workspace-copy";

/**
 * The value this inspector writes when a chart fact is absent from the
 * evidence payload.
 *
 * It is a SENTINEL, not copy: `buildReportChartSemanticInspector` compares
 * against it by value (see the `ref !== CHART_SEMANTIC_ABSENT` filter), so
 * translating it here would silently change which refs count as present.
 * The report surface therefore renders it through the locale table at the
 * RENDER SINK — the same shape as the decided thrown-`Error` policy — and the
 * report-workspace i18n gate asserts the operator never sees this string.
 */
export const CHART_SEMANTIC_ABSENT = "not recorded";

export const COGO_REPORT_CHART_SEMANTIC_INSPECTOR_SCHEMA_VERSION =
  "cogo.report-chart-semantic-inspector.v1" as const;

export type ReportChartSemanticStatus =
  | "ready"
  | "warn"
  | "blocked"
  | "missing";

export type ReportChartSemanticType =
  | "bar"
  | "line"
  | "area"
  | "pie"
  | "combo"
  | "scatter"
  | "radar"
  | "radial"
  | "unknown";

export interface ReportChartSemanticSeries {
  id: string;
  index: number;
  label: string;
  chartKind: string;
  categoryRef: string;
  valueRef: string;
  titleRef: string;
  numberFormat: string;
  axisId: string;
  styleTokenCount: number;
  formulaCaseRefs: string[];
  actionFlowCheckpointRefs: string[];
  nativeReadbackRefs: string[];
}

export interface ReportChartSemanticAxis {
  id: string;
  title: string;
  position: string;
  numberFormat: string;
  sourceRef: string;
  scale: string;
}

export interface ReportChartSemanticLegend {
  visible: boolean;
  position: string;
  overlay: boolean;
}

export interface ReportChartSemanticNativeReadbackProof {
  target: "xlsx" | "docx" | "pptx" | "hwpx" | "pdf" | "unknown";
  status: ReportChartSemanticStatus;
  evidenceRef: string;
  chartRefs: string[];
  blockers: string[];
}

export interface ReportChartSemanticUniverPreviewPlan {
  adapter: "cogo-sidecar-to-univer-sheet-preview";
  status: ReportChartSemanticStatus;
  selectedChartId: string;
  chartCount: number;
  seriesCount: number;
  rangeBindingCount: number;
  nativeReadbackProofCount: number;
}

export interface ReportChartSemanticChart {
  id: string;
  type: ReportChartSemanticType;
  title: string;
  sourceSheet: string;
  sourceChartPath: string;
  anchorRef: string;
  categoryAxis: ReportChartSemanticAxis;
  valueAxis: ReportChartSemanticAxis;
  legend: ReportChartSemanticLegend;
  seriesCount: number;
  linkedFormulaCaseCount: number;
  linkedActionFlowCount: number;
  nativeReadbackCount: number;
  series: ReportChartSemanticSeries[];
}

export interface ReportChartSemanticInspectorModel {
  schemaVersion: typeof COGO_REPORT_CHART_SEMANTIC_INSPECTOR_SCHEMA_VERSION;
  status: ReportChartSemanticStatus;
  canonicalAuthority: "cogo-chart-semantic-sidecar";
  helperPolicy: "univer-preview-helper-only";
  chartCount: number;
  seriesCount: number;
  linkedFormulaCaseCount: number;
  actionFlowBindingCount: number;
  nativeReadbackCount: number;
  charts: ReportChartSemanticChart[];
  nativeReadbackProofs: ReportChartSemanticNativeReadbackProof[];
  univerPreview: {
    enabled: boolean;
    mode:
      | "feature-flag-disabled"
      | "sidecar-backed-preview"
      | "formula-ref-fallback"
      | "missing-sidecar";
    package: "@univerjs/sheets";
    policy: "helper-not-authority";
    previewPlan?: ReportChartSemanticUniverPreviewPlan;
    blockers: string[];
  };
  blockers: string[];
  evidenceRef: string;
  sidecarRef?: string;
  sidecarResolution:
    | "embedded-payload"
    | "ref-payload-map"
    | "ref-only"
    | "missing";
}

function readRecord(value: unknown): Record<string, unknown> | null {
  return typeof value === "object" && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function readArray(value: unknown): unknown[] {
  return Array.isArray(value) ? value : [];
}

function readString(value: unknown): string | null {
  return typeof value === "string" && value.trim().length > 0 ? value : null;
}

function readBoolean(value: unknown): boolean | null {
  return typeof value === "boolean" ? value : null;
}

function readNumber(value: unknown): number | null {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function readStringList(value: unknown): string[] {
  return readArray(value).flatMap((entry) => {
    const text = readString(entry);
    return text ? [text] : [];
  });
}

function readRecordList(value: unknown): Record<string, unknown>[] {
  return readArray(value).flatMap((entry) => {
    const record = readRecord(entry);
    return record ? [record] : [];
  });
}

function unique(values: string[]): string[] {
  return [...new Set(values.filter((value) => value.trim().length > 0))];
}

function readDeep(source: unknown, path: string[]): unknown {
  let current: unknown = source;
  for (const key of path) {
    const record = readRecord(current);
    if (!record) return undefined;
    current = record[key];
  }
  return current;
}

function normalizeStatus(value: unknown): ReportChartSemanticStatus | null {
  const text = readString(value)?.toLowerCase();
  if (
    text === "ready" ||
    text === "warn" ||
    text === "blocked" ||
    text === "missing"
  ) {
    return text;
  }
  if (text === "pass" || text === "passed") return "ready";
  if (text === "fail" || text === "failed") return "blocked";
  return null;
}

function normalizeChartType(value: unknown): ReportChartSemanticType {
  const text = readString(value)?.toLowerCase();
  if (
    text === "bar" ||
    text === "line" ||
    text === "area" ||
    text === "pie" ||
    text === "combo" ||
    text === "scatter" ||
    text === "radar" ||
    text === "radial"
  ) {
    return text;
  }
  return "unknown";
}

function firstString(...values: unknown[]): string | null {
  for (const value of values) {
    const text = readString(value);
    if (text) return text;
  }
  return null;
}

function readObjectKeyCount(value: unknown): number {
  const record = readRecord(value);
  return record ? Object.keys(record).length : 0;
}

function normalizeAxis(value: unknown): ReportChartSemanticAxis {
  const record = readRecord(value);
  const scale = readRecord(record?.scale);
  return {
    id: firstString(record?.id, record?.axisId) ?? CHART_SEMANTIC_ABSENT,
    title:
      firstString(record?.title, record?.label, record?.name) ??
      CHART_SEMANTIC_ABSENT,
    position:
      firstString(record?.position, record?.axisPosition, record?.side) ??
      CHART_SEMANTIC_ABSENT,
    numberFormat:
      firstString(record?.numberFormat, record?.formatCode, record?.numFmt) ??
      CHART_SEMANTIC_ABSENT,
    sourceRef:
      firstString(record?.sourceRef, record?.rangeRef, record?.range) ??
      CHART_SEMANTIC_ABSENT,
    scale:
      firstString(scale?.orientation, record?.scale, record?.orientation) ??
      CHART_SEMANTIC_ABSENT,
  };
}

function normalizeLegend(value: unknown): ReportChartSemanticLegend {
  const record = readRecord(value);
  return {
    visible: readBoolean(record?.visible) ?? record !== null,
    position:
      firstString(record?.position, record?.legendPosition, record?.pos) ??
      CHART_SEMANTIC_ABSENT,
    overlay: readBoolean(record?.overlay) ?? false,
  };
}

function seriesRefs(entry: Record<string, unknown>, key: string): string[] {
  return unique([
    ...readStringList(entry[key]),
    ...(readString(entry[`${key.slice(0, -1)}Id`])
      ? [readString(entry[`${key.slice(0, -1)}Id`])!]
      : []),
  ]);
}

function normalizeSeries(
  entry: unknown,
  index: number,
  chartId: string,
): ReportChartSemanticSeries | null {
  const record = readRecord(entry);
  if (!record) return null;
  const id =
    firstString(record.seriesId, record.id) ?? `${chartId}-series-${index}`;
  const seriesIndex = readNumber(record.seriesIndex) ?? index;
  return {
    id,
    index: seriesIndex,
    label:
      firstString(record.name, record.label, record.title) ??
      `series ${seriesIndex + 1}`,
    chartKind:
      firstString(record.chartKind, record.type, record.kind) ?? "unknown",
    categoryRef:
      firstString(record.categoryRef, record.categoryRange, record.category) ??
      CHART_SEMANTIC_ABSENT,
    valueRef:
      firstString(record.valueRef, record.valueRange, record.values) ??
      CHART_SEMANTIC_ABSENT,
    titleRef:
      firstString(record.titleRef, record.titleRange) ?? CHART_SEMANTIC_ABSENT,
    numberFormat:
      firstString(record.numberFormat, record.formatCode, record.numFmt) ??
      CHART_SEMANTIC_ABSENT,
    axisId:
      firstString(record.axisId, record.valueAxisId) ?? CHART_SEMANTIC_ABSENT,
    styleTokenCount: readObjectKeyCount(record.styleTokens),
    formulaCaseRefs: seriesRefs(record, "formulaCaseRefs"),
    actionFlowCheckpointRefs: seriesRefs(record, "actionFlowCheckpointRefs"),
    nativeReadbackRefs: seriesRefs(record, "nativeReadbackRefs"),
  };
}

function normalizeChart(
  entry: unknown,
  index: number,
): ReportChartSemanticChart | null {
  const record = readRecord(entry);
  if (!record) return null;
  const id = firstString(record.chartId, record.id) ?? `chart-${index + 1}`;
  const series = readArray(record.series).flatMap((item, seriesIndex) => {
    const normalized = normalizeSeries(item, seriesIndex, id);
    return normalized ? [normalized] : [];
  });
  return {
    id,
    type: normalizeChartType(record.chartType ?? record.type),
    title: firstString(record.title, record.name) ?? id,
    sourceSheet:
      firstString(record.sourceSheet, record.sheet) ?? CHART_SEMANTIC_ABSENT,
    sourceChartPath:
      firstString(record.sourceChartPath, record.chartPath, record.path) ??
      CHART_SEMANTIC_ABSENT,
    anchorRef:
      firstString(record.anchorRef, record.anchor) ?? CHART_SEMANTIC_ABSENT,
    categoryAxis: normalizeAxis(record.categoryAxis),
    valueAxis: normalizeAxis(record.valueAxis),
    legend: normalizeLegend(record.legend),
    seriesCount: series.length,
    linkedFormulaCaseCount: unique(
      series.flatMap((item) => item.formulaCaseRefs),
    ).length,
    linkedActionFlowCount: unique(
      series.flatMap((item) => item.actionFlowCheckpointRefs),
    ).length,
    nativeReadbackCount: unique(
      series.flatMap((item) => item.nativeReadbackRefs),
    ).length,
    series,
  };
}

function normalizeNativeTarget(
  value: unknown,
): ReportChartSemanticNativeReadbackProof["target"] {
  const text = readString(value)?.toLowerCase();
  if (
    text === "xlsx" ||
    text === "docx" ||
    text === "pptx" ||
    text === "hwpx" ||
    text === "pdf"
  ) {
    return text;
  }
  return "unknown";
}

function normalizeNativeReadbackProof(
  value: unknown,
): ReportChartSemanticNativeReadbackProof | null {
  const record = readRecord(value);
  if (!record) return null;
  return {
    target: normalizeNativeTarget(record.target),
    status: normalizeStatus(record.status) ?? "missing",
    evidenceRef:
      firstString(record.evidenceRef, record.ref, record.path) ??
      CHART_SEMANTIC_ABSENT,
    chartRefs: unique([
      ...readStringList(record.chartRefs),
      ...(readString(record.chartRef) ? [readString(record.chartRef)!] : []),
    ]),
    blockers: readStringList(record.blockers),
  };
}

function isChartSemanticPayload(
  value: Record<string, unknown> | null,
): boolean {
  if (!value) return false;
  return (
    value.schemaVersion === "cogo.chart-semantic-sidecar.v1" ||
    Array.isArray(value.charts) ||
    readRecord(value.summary) !== null ||
    Array.isArray(value.nativeReadbackProofs)
  );
}

function normalizeHref(value: string): string {
  return value.replace(/\\/g, "/").replace(/^\.\//, "").replace(/^\/+/, "");
}

function hrefAliases(href: string): string[] {
  const normalized = normalizeHref(href);
  const parts = normalized.split("/").filter(Boolean);
  return unique([
    href,
    normalized,
    `./${normalized}`,
    parts.at(-1) ?? normalized,
  ]);
}

function payloadFromCarrier(value: unknown): Record<string, unknown> | null {
  const record = readRecord(value);
  if (isChartSemanticPayload(record)) return record;
  for (const key of ["payload", "data", "json", "content", "sidecar"]) {
    const nested = readRecord(record?.[key]);
    if (isChartSemanticPayload(nested)) return nested;
  }
  return null;
}

function carrierHref(value: Record<string, unknown>): string | null {
  return firstString(
    value.href,
    value.path,
    value.relativePath,
    value.outputPath,
    value.artifactPath,
    value.downloadPath,
  );
}

function carrierMatchesHref(
  value: Record<string, unknown>,
  aliases: string[],
): boolean {
  const href = carrierHref(value);
  if (!href) return false;
  const normalized = normalizeHref(href);
  return aliases.some((alias) => normalizeHref(alias) === normalized);
}

interface ChartSemanticRefDetails {
  href?: string;
  sha256?: string;
  ref: string;
}

function chartSemanticRefDetails(
  project: unknown,
): ChartSemanticRefDetails | null {
  const candidates: Array<{ value: unknown; ref: string }> = [
    { value: readDeep(project, ["chartSemanticRef"]), ref: "chartSemanticRef" },
    {
      value: readDeep(project, ["metadata", "chartSemanticRef"]),
      ref: "metadata.chartSemanticRef",
    },
    {
      value: readDeep(project, ["manifest", "chartSemanticRef"]),
      ref: "manifest.chartSemanticRef",
    },
    {
      value: readDeep(project, ["documentModel", "chartSemanticRef"]),
      ref: "documentModel.chartSemanticRef",
    },
    {
      value: readDeep(project, ["metadata", "chartSemanticSidecar"]),
      ref: "metadata.chartSemanticSidecar",
    },
    {
      value: readDeep(project, ["manifest", "chartSemanticSidecar"]),
      ref: "manifest.chartSemanticSidecar",
    },
    {
      value: readDeep(project, ["documentModel", "chartSemanticSidecar"]),
      ref: "documentModel.chartSemanticSidecar",
    },
  ];
  for (const candidate of candidates) {
    const text = readString(candidate.value);
    if (text) return { href: text, ref: candidate.ref };
    const record = readRecord(candidate.value);
    if (!record || isChartSemanticPayload(record)) continue;
    const href = firstString(record.href, record.path, record.relativePath);
    const sha256 = firstString(record.sha256, record.sidecarSha256);
    if (href || sha256)
      return {
        ...(href ? { href } : {}),
        ...(sha256 ? { sha256 } : {}),
        ref: candidate.ref,
      };
  }
  return null;
}

function chartSemanticPayloadByRef(
  project: unknown,
  href: string,
): {
  evidence: Record<string, unknown> | null;
  evidenceRef: string;
} {
  const aliases = hrefAliases(href);
  const mapPaths = [
    ["chartSemanticSidecarPayloads"],
    ["sidecarPayloads"],
    ["documentSidecarPayloads"],
    ["metadata", "chartSemanticSidecarPayloads"],
    ["metadata", "sidecarPayloads"],
    ["metadata", "documentSidecarPayloads"],
    ["manifest", "chartSemanticSidecarPayloads"],
    ["manifest", "sidecarPayloads"],
    ["manifest", "documentSidecarPayloads"],
    ["documentModel", "sidecarPayloads"],
    ["generationEvidence", "sidecarPayloads"],
    ["documentExportProductionGate", "sidecarPayloads"],
  ];
  for (const path of mapPaths) {
    const map = readRecord(readDeep(project, path));
    if (!map) continue;
    for (const alias of aliases) {
      const payload = payloadFromCarrier(map[alias]);
      if (payload) {
        return { evidence: payload, evidenceRef: `${path.join(".")}.${alias}` };
      }
    }
  }

  const arrayPaths = [
    ["sidecars"],
    ["documentSidecars"],
    ["metadata", "sidecars"],
    ["metadata", "documentSidecars"],
    ["manifest", "sidecars"],
    ["manifest", "documentSidecars"],
    ["documentModel", "sidecars"],
    ["documentModel", "documentSidecars"],
    ["generationEvidence", "sidecars"],
    ["documentExportProductionGate", "sidecars"],
  ];
  for (const path of arrayPaths) {
    for (const [index, carrier] of readRecordList(
      readDeep(project, path),
    ).entries()) {
      if (
        readString(carrier.kind) !== "chart-semantic-sidecar" &&
        !carrierMatchesHref(carrier, aliases)
      ) {
        continue;
      }
      const payload = payloadFromCarrier(carrier);
      if (payload) {
        return {
          evidence: payload,
          evidenceRef: `${path.join(".")}[${index}]`,
        };
      }
    }
  }
  return { evidence: null, evidenceRef: "" };
}

function chartSemanticEvidence(project: unknown): {
  evidence: Record<string, unknown> | null;
  evidenceRef: string;
  sidecarResolution: ReportChartSemanticInspectorModel["sidecarResolution"];
} {
  const candidates: Array<{ path: string[]; ref: string }> = [
    { path: ["chartSemanticSidecar"], ref: "chartSemanticSidecar" },
    { path: ["chartSemantic"], ref: "chartSemantic" },
    {
      path: ["metadata", "chartSemanticSidecar"],
      ref: "metadata.chartSemanticSidecar",
    },
    { path: ["metadata", "chartSemantic"], ref: "metadata.chartSemantic" },
    {
      path: ["manifest", "chartSemanticSidecar"],
      ref: "manifest.chartSemanticSidecar",
    },
    { path: ["manifest", "chartSemantic"], ref: "manifest.chartSemantic" },
    {
      path: ["generationEvidence", "chartSemanticSidecar"],
      ref: "generationEvidence.chartSemanticSidecar",
    },
    {
      path: ["documentExportProductionGate", "chartSemanticSidecar"],
      ref: "documentExportProductionGate.chartSemanticSidecar",
    },
  ];
  for (const candidate of candidates) {
    const evidence = readRecord(readDeep(project, candidate.path));
    if (isChartSemanticPayload(evidence)) {
      return {
        evidence,
        evidenceRef: candidate.ref,
        sidecarResolution: "embedded-payload",
      };
    }
  }
  const ref = chartSemanticRefDetails(project);
  if (ref?.href) {
    const resolved = chartSemanticPayloadByRef(project, ref.href);
    if (resolved.evidence) {
      return {
        ...resolved,
        sidecarResolution: "ref-payload-map",
      };
    }
    return {
      evidence: null,
      evidenceRef: ref.ref,
      sidecarResolution: "ref-only",
    };
  }
  return { evidence: null, evidenceRef: "", sidecarResolution: "missing" };
}

function chartSemanticRef(project: unknown): string | undefined {
  return chartSemanticRefDetails(project)?.href;
}

function previewFlag(
  project: unknown,
  evidence: Record<string, unknown> | null,
): boolean {
  const candidates = [
    readDeep(project, ["metadata", "features", "univerChartPreview"]),
    readDeep(project, ["metadata", "univerChartPreviewEnabled"]),
    readDeep(project, ["manifest", "features", "univerChartPreview"]),
    readDeep(evidence, ["univerPreview", "enabled"]),
  ];
  return candidates.some((candidate) => readBoolean(candidate) === true);
}

function modelFromEvidence(
  project: unknown,
  evidence: Record<string, unknown>,
  evidenceRef: string,
  sidecarResolution: ReportChartSemanticInspectorModel["sidecarResolution"],
  copy: ReportWorkspaceModelCopy,
): ReportChartSemanticInspectorModel {
  const charts = readArray(evidence.charts).flatMap((entry, index) => {
    const chart = normalizeChart(entry, index);
    return chart ? [chart] : [];
  });
  const summary = readRecord(evidence.summary);
  const evidenceBlockers = readStringList(evidence.blockers);
  const nativeReadbackProofs = readArray(evidence.nativeReadbackProofs).flatMap(
    (entry) => {
      const proof = normalizeNativeReadbackProof(entry);
      return proof ? [proof] : [];
    },
  );
  const chartCount = readNumber(summary?.chartCount) ?? charts.length;
  const seriesCount =
    readNumber(summary?.seriesCount) ??
    charts.reduce((total, chart) => total + chart.seriesCount, 0);
  const linkedFormulaCaseCount =
    readNumber(summary?.linkedFormulaCaseCount) ??
    unique(
      charts.flatMap((chart) =>
        chart.series.flatMap((series) => series.formulaCaseRefs),
      ),
    ).length;
  const actionFlowBindingCount =
    readNumber(summary?.actionFlowBindingCount) ??
    unique(
      charts.flatMap((chart) =>
        chart.series.flatMap((series) => series.actionFlowCheckpointRefs),
      ),
    ).length;
  const nativeReadbackCount =
    readNumber(summary?.nativeReadbackCount) ??
    Math.max(
      unique(
        charts.flatMap((chart) =>
          chart.series.flatMap((series) => series.nativeReadbackRefs),
        ),
      ).length,
      nativeReadbackProofs.length,
    );
  const blockers = [
    ...evidenceBlockers,
    chartCount > 0 && seriesCount === 0
      ? copy.blockerChartNoSeriesBindings
      : null,
  ].filter((value): value is string => Boolean(value));
  const enabled = previewFlag(project, evidence);
  const previewPlan: ReportChartSemanticUniverPreviewPlan | undefined = enabled
    ? {
        adapter: "cogo-sidecar-to-univer-sheet-preview",
        status: chartCount > 0 && seriesCount > 0 ? "ready" : "blocked",
        selectedChartId: charts[0]?.id ?? "missing",
        chartCount,
        seriesCount,
        rangeBindingCount: charts.flatMap((chart) =>
          chart.series.flatMap((series) =>
            [series.categoryRef, series.valueRef].filter(
              (ref) => ref !== CHART_SEMANTIC_ABSENT,
            ),
          ),
        ).length,
        nativeReadbackProofCount: nativeReadbackProofs.length,
      }
    : undefined;
  return {
    schemaVersion: COGO_REPORT_CHART_SEMANTIC_INSPECTOR_SCHEMA_VERSION,
    status:
      normalizeStatus(evidence.status) ??
      (blockers.length > 0 ? "blocked" : chartCount > 0 ? "ready" : "missing"),
    canonicalAuthority: "cogo-chart-semantic-sidecar",
    helperPolicy: "univer-preview-helper-only",
    chartCount,
    seriesCount,
    linkedFormulaCaseCount,
    actionFlowBindingCount,
    nativeReadbackCount,
    charts,
    nativeReadbackProofs,
    univerPreview: {
      enabled,
      mode: enabled ? "sidecar-backed-preview" : "feature-flag-disabled",
      package: "@univerjs/sheets",
      policy: "helper-not-authority",
      ...(previewPlan ? { previewPlan } : {}),
      blockers: enabled
        ? previewPlan?.status === "ready"
          ? []
          : [
              "Univer chart preview needs at least one chart and series binding.",
            ]
        : ["Univer chart preview feature flag is disabled."],
    },
    blockers,
    evidenceRef: readString(evidence.evidenceRef) ?? evidenceRef,
    sidecarRef: chartSemanticRef(project),
    sidecarResolution,
  };
}

function formulaChartRefs(
  cases: CogoUniverFormulaOracleCase[],
): ReportChartSemanticChart[] {
  const byChart = new Map<string, ReportChartSemanticSeries[]>();
  for (const formulaCase of cases) {
    for (const ref of formulaCase.chartSeriesRefs ?? []) {
      const record = readRecord(ref);
      const chartId = firstString(record?.chartId, record?.id) ?? "chart";
      const seriesIndex = readNumber(record?.seriesIndex) ?? 0;
      const role = firstString(record?.role) ?? "values";
      const series: ReportChartSemanticSeries = {
        id: `${chartId}-series-${seriesIndex}-${role}`,
        index: seriesIndex,
        label: `${formulaCase.label} ${role}`,
        chartKind: "unknown",
        categoryRef:
          role === "category"
            ? (firstString(record?.formula) ?? CHART_SEMANTIC_ABSENT)
            : CHART_SEMANTIC_ABSENT,
        valueRef:
          role !== "category"
            ? (firstString(record?.formula) ?? CHART_SEMANTIC_ABSENT)
            : CHART_SEMANTIC_ABSENT,
        titleRef: CHART_SEMANTIC_ABSENT,
        numberFormat: CHART_SEMANTIC_ABSENT,
        axisId: CHART_SEMANTIC_ABSENT,
        styleTokenCount: 0,
        formulaCaseRefs: [formulaCase.id],
        actionFlowCheckpointRefs: formulaCase.actionFlowCheckpoint
          ? [
              `${formulaCase.actionFlowCheckpoint.flowId}.${formulaCase.actionFlowCheckpoint.stepId}`,
            ]
          : [],
        nativeReadbackRefs: [],
      };
      byChart.set(chartId, [...(byChart.get(chartId) ?? []), series]);
    }
  }
  return Array.from(byChart.entries()).map(([id, series]) => ({
    id,
    type: "unknown",
    title: id,
    sourceSheet: "formula-oracle",
    sourceChartPath: CHART_SEMANTIC_ABSENT,
    anchorRef: CHART_SEMANTIC_ABSENT,
    categoryAxis: normalizeAxis(null),
    valueAxis: normalizeAxis(null),
    legend: normalizeLegend(null),
    seriesCount: series.length,
    linkedFormulaCaseCount: unique(
      series.flatMap((item) => item.formulaCaseRefs),
    ).length,
    linkedActionFlowCount: unique(
      series.flatMap((item) => item.actionFlowCheckpointRefs),
    ).length,
    nativeReadbackCount: 0,
    series,
  }));
}

function fallbackFromFormulaOracle(
  formulaOracle: CogoUniverFormulaOracleModel | undefined,
  copy: ReportWorkspaceModelCopy,
): ReportChartSemanticInspectorModel | null {
  if (!formulaOracle) return null;
  const charts = formulaChartRefs(formulaOracle.cases);
  const graphChartCount = formulaOracle.calculationGraph?.chartCount ?? 0;
  const graphSeriesCount =
    formulaOracle.calculationGraph?.chartSeriesCount ?? 0;
  if (charts.length === 0 && graphChartCount === 0 && graphSeriesCount === 0) {
    return null;
  }
  const seriesCount =
    charts.reduce((total, chart) => total + chart.seriesCount, 0) ||
    graphSeriesCount;
  return {
    schemaVersion: COGO_REPORT_CHART_SEMANTIC_INSPECTOR_SCHEMA_VERSION,
    status: "warn",
    canonicalAuthority: "cogo-chart-semantic-sidecar",
    helperPolicy: "univer-preview-helper-only",
    chartCount: charts.length || graphChartCount,
    seriesCount,
    linkedFormulaCaseCount: unique(
      charts.flatMap((chart) =>
        chart.series.flatMap((series) => series.formulaCaseRefs),
      ),
    ).length,
    actionFlowBindingCount: unique(
      charts.flatMap((chart) =>
        chart.series.flatMap((series) => series.actionFlowCheckpointRefs),
      ),
    ).length,
    nativeReadbackCount: 0,
    charts,
    nativeReadbackProofs: [],
    univerPreview: {
      enabled: false,
      mode: "formula-ref-fallback",
      package: "@univerjs/sheets",
      policy: "helper-not-authority",
      blockers: [copy.blockerChartDataMissing],
    },
    blockers: [copy.blockerChartDataMissingFormulaOnly],
    evidenceRef:
      formulaOracle.calculationGraph?.evidenceRef || formulaOracle.evidenceRef,
    sidecarResolution: "missing",
  };
}

export function buildReportChartSemanticInspector(
  project: unknown,
  formulaOracle?: CogoUniverFormulaOracleModel,
  /** Defaults to `en` so every existing caller keeps the output it asserts. */
  copy: ReportWorkspaceModelCopy = getReportWorkspaceModelCopy("en"),
): ReportChartSemanticInspectorModel {
  const { evidence, evidenceRef, sidecarResolution } =
    chartSemanticEvidence(project);
  if (evidence) {
    return modelFromEvidence(
      project,
      evidence,
      evidenceRef,
      sidecarResolution,
      copy,
    );
  }
  const fallback = fallbackFromFormulaOracle(formulaOracle, copy);
  if (fallback) return fallback;
  const sidecarRef = chartSemanticRef(project);
  const refOnlyBlockers = sidecarRef
    ? [copy.blockerChartDataNotLoaded]
    : [copy.blockerChartDataMissing];
  return {
    schemaVersion: COGO_REPORT_CHART_SEMANTIC_INSPECTOR_SCHEMA_VERSION,
    status: sidecarRef ? "warn" : "missing",
    canonicalAuthority: "cogo-chart-semantic-sidecar",
    helperPolicy: "univer-preview-helper-only",
    chartCount: 0,
    seriesCount: 0,
    linkedFormulaCaseCount: 0,
    actionFlowBindingCount: 0,
    nativeReadbackCount: 0,
    charts: [],
    nativeReadbackProofs: [],
    univerPreview: {
      enabled: false,
      mode: "missing-sidecar",
      package: "@univerjs/sheets",
      policy: "helper-not-authority",
      blockers: refOnlyBlockers,
    },
    blockers: refOnlyBlockers,
    evidenceRef: sidecarRef ? "chartSemanticRef" : "missing",
    ...(sidecarRef ? { sidecarRef } : {}),
    sidecarResolution,
  };
}
